import i18n from 'i18next';
import HttpBackend, { type HttpBackendOptions } from 'i18next-http-backend';
import ICU from 'i18next-icu';

import { PropsWithChildren, Suspense } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';

import { AppLoader, LocalizationProvider } from '@ingrid/luterkort-react';

import { AppFetchConfig } from '~src/main/config';

import enDev from './en_dev.json';
import { useTranslation } from './use-translation';

interface AppLanguage {
  code: string;
  nativeName: string;
}

export const AppLanguages: AppLanguage[] = [{ code: 'en-GB', nativeName: 'English' }];

export const DefaultLanguage = AppLanguages[0];

export function I18nProvider({ children }: PropsWithChildren) {
  i18n
    .use(HttpBackend)
    .use(initReactI18next)
    .use(ICU)
    .init<HttpBackendOptions>({
      // Local dev specific
      ...(import.meta.env.DEV && {
        debug: true,
        resources: {
          [DefaultLanguage.code]: { translation: enDev },
        },
      }),

      // Language loading
      backend: {
        loadPath: `${AppFetchConfig.cdnUrl}/transport-locales/{lng}.json`,
        requestOptions: {
          method: 'get',
        },
      },
      fallbackLng: DefaultLanguage.code,

      // Parsing
      interpolation: {
        prefix: '{',
        suffix: '}',
      },

      lng: localStorage.getItem('lng') ?? DefaultLanguage.code,
      load: 'currentOnly',
      react: {
        useSuspense: false,
      },

      returnEmptyString: false,
      supportedLngs: AppLanguages.map(lng => lng.code),
    })
    .catch(err => console.error(err));

  return (
    <I18nextProvider i18n={i18n}>
      <Suspense fallback={<AppLoader />}>
        <LuterkortLocalizationProvider>{children}</LuterkortLocalizationProvider>
      </Suspense>
    </I18nextProvider>
  );
}

function LuterkortLocalizationProvider({ children }: PropsWithChildren) {
  const { i18n } = useTranslation();

  return (
    <LocalizationProvider host={AppFetchConfig.cdnUrl} locale={i18n.resolvedLanguage ?? DefaultLanguage.code}>
      {children}
    </LocalizationProvider>
  );
}
